import { useMemo } from "react";
import { useRequest } from "@/request";
import type { PagedResultDto } from "@/types/abp";

/** 客服组范围类型（对应后端 TeamScopeType）。 */
export const TeamScopeType = {
  ProjectScoped: 1,
  Functional: 2,
} as const;
export type TeamScopeType = (typeof TeamScopeType)[keyof typeof TeamScopeType];

/** 分派策略（对应后端 AssignmentPolicy）。 */
export const AssignmentPolicy = {
  RoundRobin: 1,
  Manual: 2,
  Claim: 3,
} as const;
export type AssignmentPolicy = (typeof AssignmentPolicy)[keyof typeof AssignmentPolicy];

/** 客服组状态（对应后端 TeamStatus）。 */
export const TeamStatus = {
  Active: 1,
  Inactive: 2,
} as const;
export type TeamStatus = (typeof TeamStatus)[keyof typeof TeamStatus];

/** 成员在组内角色（对应后端 MembershipRole）。 */
export const MembershipRole = {
  Member: 1,
  Lead: 2,
} as const;
export type MembershipRole = (typeof MembershipRole)[keyof typeof MembershipRole];

export type TeamMemberDto = {
  agentId: string;
  role: MembershipRole;
};

/** 客服组（对应 Api.Admin TeamDto，JSON 为 camelCase）。 */
export type TeamDto = {
  id: string;
  name: string;
  scopeType: TeamScopeType;
  projectId: string | null;
  assignmentPolicy: AssignmentPolicy;
  status: TeamStatus;
  members: TeamMemberDto[];
  concurrencyStamp: string;
};

export type TeamListRequest = {
  projectId?: string | null;
  onlyActive?: boolean;
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
};

export type CreateTeamInput = {
  name: string;
  scopeType: TeamScopeType;
  projectId?: string | null;
  assignmentPolicy: AssignmentPolicy;
};

export type UpdateTeamInput = {
  name: string;
  scopeType: TeamScopeType;
  projectId?: string | null;
  assignmentPolicy: AssignmentPolicy;
  concurrencyStamp: string;
};

export type TeamMemberInput = {
  agentId: string;
  role: MembershipRole;
};

/**
 * 添加组内成员。`agentId` 与 `iamSubject` 二选一：
 * 给 subject 时后端会先从 IAM 目录开通再入组，一步完成「搜人 → 选角色 → 完成」。
 */
export type AddTeamMemberInput = {
  agentId?: string;
  iamSubject?: string;
  role: MembershipRole;
};

function buildQuery(request: TeamListRequest): string {
  const params = new URLSearchParams();
  if (request.projectId) {
    params.set("ProjectId", request.projectId);
  }
  if (request.onlyActive !== undefined) {
    params.set("OnlyActive", String(request.onlyActive));
  }
  if (request.skipCount !== undefined) {
    params.set("SkipCount", String(request.skipCount));
  }
  if (request.maxResultCount !== undefined) {
    params.set("MaxResultCount", String(request.maxResultCount));
  }
  if (request.sorting) {
    params.set("Sorting", request.sorting);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** 客服组 CRUD + 成员管理客户端（挂在 Api.Admin `teams` 端点上）。 */
export function useTeamApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      getList: (input: TeamListRequest = {}) => request<PagedResultDto<TeamDto>>(`teams${buildQuery(input)}`, { method: "GET", skipErrorToast: true }),
      create: (input: CreateTeamInput) => request<TeamDto>("teams", { method: "POST", data: input }),
      update: (id: string, input: UpdateTeamInput) => request<TeamDto>(`teams/${id}`, { method: "PUT", data: input }),
      setActive: (id: string, active: boolean) => request<TeamDto>(`teams/${id}/active?active=${active}`, { method: "POST" }),
      remove: (id: string) => request<void>(`teams/${id}`, { method: "DELETE" }),
      addMember: (id: string, input: AddTeamMemberInput) => request<TeamDto>(`teams/${id}/members`, { method: "POST", data: input }),
      changeMemberRole: (id: string, input: TeamMemberInput) => request<TeamDto>(`teams/${id}/members`, { method: "PUT", data: input }),
      removeMember: (id: string, agentId: string) => request<TeamDto>(`teams/${id}/members/${agentId}`, { method: "DELETE" }),
    }),
    [request],
  );
}
